import { TAO_KOE, TaoStorage } from './../libs';

/**
 * Menu Class
 *
 * @param {string} target
 */

class Menu {
	constructor( containerEl ) {
		this.containerEl = containerEl;

		// Preload HTML data.
		this.preload();

		// Initialise Submenus.
		const subMenuEls = this.containerEl.querySelectorAll( '.mdl_mainmenu__root-item--has-submenu' );
		this.MenuItems = Array.from( subMenuEls ).map( ( menuEl ) => {
			menuEl.KOE_MENU_ITEM ??= new MenuItem( menuEl );
			return menuEl.KOE_MENU_ITEM;
		} );

		this.containerEl.addEventListener( 'mouseover', ( ev ) => this.onUserInteraction( ev, 'open' ) );
		this.containerEl.addEventListener( 'mouseout', ( ev ) => this.onUserInteraction( ev, 'close' ) );

		this.containerEl.addEventListener( 'focusin', ( ev ) => this.onUserInteraction( ev, 'open' ) );
		this.containerEl.addEventListener( 'focusout', ( ev ) => this.onUserInteraction( ev, 'close' ) );

		this.containerEl.addEventListener( 'before_open_submenu.koe', ( ev ) => {
			// Close all submenus except the one we are opening right now.
			this.MenuItems.filter( ( Item ) => Item.menuItemEl !== ev.target ).forEach( ( Item ) => Item.close() );
		} );
	}

	getMenuItem( postID ) {
		return this.MenuItems.find( ( Item ) => [ Item.menuItemEl.dataset.nav ].includes( postID ) );
	}

	onUserInteraction( ev, action = 'open' ) {
		const targetEl = ev.target;
		if ( targetEl.closest( '.mdl_mainmenu__root-item' ) ) {
			const itemEl = targetEl.closest( '.mdl_mainmenu__root-item' );
			const Item = this.getMenuItem( itemEl.dataset.nav );
			if ( Item ) {
				// Open or close.
				Item[ action ]();
			}
		} else if ( targetEl.closest( '.mdl_mainmenu__sub-ul' ) ) {
			const submenuEl = targetEl.closest( '.mdl_mainmenu__sub-ul' );
			const Item = this.getMenuItem( submenuEl.dataset.nav );
			if ( Item ) {
				// Open or close.
				Item[ action ]();
			}
		}
	}

	loading = false;

	async preload( callback ) {
		const mycallback = 'function' === typeof callback ? callback : function() {};

		const storage = Menu.getStorage();
		if ( ! storage ) {
			return;
		}

		if ( ! this.hasUpdate() ) {
			mycallback( storage );
			return;
		}

		if ( this.loading ) {
			return;
		}

		this.loading = true;

		return await fetch( TAO_KOE.getDispatchUrl( '/dispatch/menu/all/' ) )
			.then( ( res ) => res.json() )
			.then( ( response ) => {
				if ( response.time ) {
					storage.setItem( 'mainmenu_timestamp', response.time );
				}

				for ( const postID in response.menu ) {
					if ( response.menu.hasOwnProperty( postID ) ) {
						const content = response.menu[ postID ];
						storage.setItem( 'mainmenu_' + postID, content );
					}
				}

				mycallback( storage );
			} )
			.finally( () => {
				this.loading = false;
			} );
	}

	hasUpdate() {
		const storage = Menu.getStorage();
		const savedTimestamp = parseInt( storage.getItem( 'mainmenu_timestamp' ) );
		const { MenuTimestamp } = TAO_KOE;

		if ( ! savedTimestamp ) {
			return true;
		}

		if ( 'undefined' === typeof MenuTimestamp ) {
			return true;
		}

		return MenuTimestamp > savedTimestamp;
	}

	static getStorage = function() {
		return TaoStorage.getLocalStorage( 'menu' );
	};
}

class MenuItem {
	loading = false;

	constructor( menuItemEl ) {
		this.menuItemEl = menuItemEl;

		menuItemEl.addEventListener( 'mouseenter', () => this.load( menuItemEl ), { once: true } );
		menuItemEl.addEventListener( 'focusin', () => this.load( menuItemEl ), { once: true } );

		// Should we load the item immediately?
		if ( menuItemEl.matches( ':hover' ) || menuItemEl.matches( ':focus' ) ) {
			this.open();
		}
	}

	async load() {
		const subMenuContainerEl = this.getSubmenuContainer();
		const postID = this.menuItemEl.dataset.nav;
		const storage = Menu.getStorage();

		if ( ! postID ) {
			return true;
		}

		if ( this.getSubmenuElement() ) {
			return true;
		}

		if ( this.loading ) {
			return true;
		}

		if ( storage ) {
			const cachedContent = String( storage.getItem( 'mainmenu_' + postID ) );
			if ( 'undefined' === cachedContent || 'null' === cachedContent || 'false' === cachedContent ) {
				// Do nothing.
			} else {
				subMenuContainerEl.insertAdjacentHTML( 'beforeend', cachedContent );
				this.onLoad();
				return true;
			}
		}

		// Generate fetch url.
		const fetchUrl = new URL( TAO_KOE.getDispatchUrl( '/dispatch/menu/submenu/' ) );
		fetchUrl.searchParams.append( 'post_id', postID );

		// Indicate, that this menu is currently loading, so we don't trigger twice.
		this.loading = true;

		return await fetch( fetchUrl )
			.then( ( res ) => res.text() )
			.then( ( newContent ) => {
				if ( storage && newContent ) {
					storage.setItem( 'mainmenu_' + postID, newContent );
				}

				subMenuContainerEl.insertAdjacentHTML( 'beforeend', newContent );
				this.onLoad();
				return newContent;
			} ).finally( () => {
				this.loading = false;
			} );
	}

	onLoad() {
		setTimeout( () => {
			this.menuItemEl.dispatchEvent( new CustomEvent( 'load_submenu.koe', { bubbles: true, detail: { 'subMenu': this.getSubmenuElement() } } ) );
		}, 0 );
	}

	getSubmenuContainer() {
		const containerEl = this.menuItemEl.closest( '.mdl_mainmenu' );
		return containerEl.querySelector( '.mdl_mainmenu__sub' );
	}

	getSubmenuElement() {
		const postID = this.menuItemEl.dataset.nav;
		if ( postID ) {
			const subMenuContainerEl = this.getSubmenuContainer();
			return subMenuContainerEl.querySelector( '.mdl_mainmenu__sub-ul[data-nav="' + postID + '"]' );
		}
		return null;
	}

	open() {
		// Trigger event before action.
		this.menuItemEl.dispatchEvent( new CustomEvent( 'before_open_submenu.koe', { bubbles: true } ) );

		this.isOpen = true;
		this.menuItemEl.classList.add( 'is-hovered' );
		this.load().then( () => {
			// Do we still want to open after timeout?
			if ( this.isOpen ) {
				const subMenu = this.getSubmenuElement();
				if ( subMenu ) {
					subMenu.classList.add( 'is-active' );
				}
			}
		} );
	}

	close() {
		// Trigger event before action.
		this.menuItemEl.dispatchEvent( new CustomEvent( 'before_close_submenu.koe', { bubbles: true } ) );

		this.isOpen = false;
		setTimeout( () => {
			// Do we still want to close after timeout?
			if ( ! this.isOpen ) {
				this.menuItemEl.classList.remove( 'is-hovered' );

				const subMenu = this.getSubmenuElement();
				if ( subMenu ) {
					subMenu.classList.remove( 'is-active' );
				}
			}
		}, 0 );
	}
}

/**
 * Initialise.
 */
const initMenu = function() {
	if ( TAO_KOE.MenuDisable ) {
		return;
	}

	// Initialise Mainmenu.
	const containerEls = document.querySelectorAll( '.mdl_mainmenu' );
	containerEls.forEach( ( containerEl ) => {
		containerEl.KOE_MENU ??= new Menu( containerEl );
	} );
};

// Init.
TAO_KOE.ready( 'menu.js', initMenu );

// Expose.
TAO_KOE.Menu = Menu;
export {
	Menu as default,
	Menu,
	MenuItem,
	initMenu,
};
